<template>
  <div class="emr-dashboard-page bg-light-gray">
    <!-- Header component -->
    <the-header ref="theHeader" />

    <!-- Main Content Container -->
    <main class="emr-dashboard-container py-4">
      <b-container fluid class="px-md-5">

        <!-- Welcome Banner / Header Section -->
        <b-row class="mb-4">
          <b-col cols="12">
            <div class="dashboard-banner p-4 rounded-lg shadow-sm bg-white border-left-teal">
              <div class="d-flex justify-content-between align-items-start flex-wrap">
                <div>
                  <h1 class="dashboard-title text-teal font-weight-bold mb-1">
                    <b-icon icon="building" class="mr-2" />EMR Facilities Dashboard
                  </h1>
                  <p class="dashboard-subtitle text-muted mb-0">
                    Real-time monitoring and analytics of Electronic Medical Record (EMR) implementation across healthcare facilities in Nigeria.
                  </p>
                </div>
                <!-- Source Configuration Controls -->
                <div class="mt-3 mt-md-0 d-flex gap-2 align-items-center">
                  <b-button
                    variant="teal"
                    size="sm"
                    class="d-flex align-items-center"
                    :disabled="loading"
                    @click="fetchData"
                  >
                    <b-icon icon="arrow-clockwise" :class="{ 'spin-icon': loading }" class="mr-1" /> Reload Data
                  </b-button>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Loading State -->
        <b-row v-if="loading" class="my-5 py-5">
          <b-col cols="12" class="text-center">
            <b-spinner variant="teal" label="Spinning" class="mb-3" style="width: 3rem; height: 3rem;"></b-spinner>
            <h4 class="text-teal font-weight-bold">Fetching EMR facilities data...</h4>
            <p class="text-muted">Parsing spreadsheet entries and calculating visualizations.</p>
          </b-col>
        </b-row>

        <!-- Main Dashboard View -->
        <template v-else>
          <!-- Error Alert -->
          <b-row v-if="error" class="mb-4">
            <b-col cols="12">
              <b-alert show variant="danger" class="border-danger-light mb-0 shadow-sm">
                <b-icon icon="exclamation-triangle-fill" class="mr-2" />
                <strong>Failed to load spreadsheet:</strong> {{ error }}. Showing pre-loaded mock data instead.
              </b-alert>
            </b-col>
          </b-row>

          <!-- Filters Control Panel Row -->
          <b-row class="mb-4">
            <b-col cols="12">
              <div class="control-panel-card shadow-sm bg-white p-4 rounded-lg border">
                <div class="d-flex justify-content-between align-items-center flex-wrap mb-3 border-bottom pb-2">
                  <h5 class="control-panel-title text-teal font-weight-bold mb-0">
                    <b-icon icon="sliders" class="mr-2" />Filter Controls
                  </h5>
                  <div class="d-flex gap-2">
                    <b-button
                      :variant="layerStatus ? 'teal' : 'outline-teal'"
                      size="sm"
                      class="mr-2"
                      @click="layerStatus = !layerStatus"
                    >
                      <b-icon :icon="layerStatus ? 'layers-fill' : 'layers'" class="mr-1" />
                      {{ layerStatus ? 'Layering Active' : 'Layer EMR Status' }}
                    </b-button>
                    <b-button variant="teal" size="sm" @click="exportToCSV" class="mr-2">
                      <b-icon icon="download" class="mr-1" /> Export CSV
                    </b-button>
                    <b-button variant="outline-dark" size="sm" @click="resetFilters">
                      Reset Filters
                    </b-button>
                  </div>
                </div>

                <b-row>
                  <b-col cols="12" sm="6" md="4" lg="2" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">State</label>
                    <b-form-select v-model="filters.state" class="custom-premium-select" :options="stateOptions" />
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="2" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">LGA</label>
                    <b-form-select v-model="filters.lga" class="custom-premium-select" :options="lgaOptions" />
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="2" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">Level of Care</label>
                    <b-form-select v-model="filters.levelOfCare" class="custom-premium-select" :options="levelOfCareOptions" />
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="2" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">Ownership</label>
                    <b-form-select v-model="filters.ownership" class="custom-premium-select" :options="ownershipOptions" />
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="2" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">BHCPF Facility</label>
                    <b-form-select v-model="filters.bhcpf" class="custom-premium-select" :options="bhcpfOptions" />
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="2" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">EMR Status</label>
                    <b-form-select v-model="filters.status" class="custom-premium-select" :options="statusOptions" />
                  </b-col>
                  <b-col cols="12" class="mb-2">
                    <label class="filter-label text-uppercase text-muted font-weight-bold mb-1">Keyword Search</label>
                    <b-form-input
                      v-model="filters.search"
                      class="custom-premium-input"
                      placeholder="Search Facility, Assignee or EMR ID..."
                    />
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>

          <!-- Metric Cards Section -->
          <b-row class="mb-4">
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="metric-card shadow-sm p-3 bg-white rounded-lg border-bottom-teal">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="metric-label text-muted text-uppercase mb-1">Total Facilities</p>
                    <h3 class="metric-value font-weight-bold mb-0 text-dark">{{ metrics.totalFacilities }}</h3>
                  </div>
                  <div class="metric-icon-box bg-teal-light text-teal">
                    <b-icon icon="building" font-scale="1.2" />
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="metric-card shadow-sm p-3 bg-white rounded-lg border-bottom-blue">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="metric-label text-muted text-uppercase mb-1">States Covered</p>
                    <h3 class="metric-value font-weight-bold mb-0 text-dark">{{ metrics.totalStates }}</h3>
                  </div>
                  <div class="metric-icon-box bg-blue-light text-blue">
                    <b-icon icon="map" font-scale="1.2" />
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="metric-card shadow-sm p-3 bg-white rounded-lg border-bottom-green">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="metric-label text-muted text-uppercase mb-1">
                      Active EMRs(Fully Functional)
                      <b-icon
                        icon="info-circle-fill"
                        class="ml-1 text-muted cursor-pointer"
                        v-b-tooltip.hover
                        title="Includes facilities with 'Fully Functional' or 'Active' EMR status."
                      />
                    </p>
                    <h3 class="metric-value font-weight-bold mb-0 text-dark">
                      {{ metrics.activeEMR }}
                      <span class="metric-pct font-weight-normal text-muted" style="font-size: 13px;">
                        ({{ metrics.activeEMRPct }}%)
                      </span>
                    </h3>
                  </div>
                  <div class="metric-icon-box bg-green-light text-green">
                    <b-icon icon="check-circle" font-scale="1.2" />
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
              <div class="metric-card shadow-sm p-3 bg-white rounded-lg border-bottom-orange">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="metric-label text-muted text-uppercase mb-1">
                      In-Progress / Pending
                      <b-icon
                        icon="info-circle-fill"
                        class="ml-1 text-muted cursor-pointer"
                        v-b-tooltip.hover
                        title="Includes facilities with 'Partially functional' or 'yet to commence' EMR status, or with no status recorded."
                      />
                    </p>
                    <h3 class="metric-value font-weight-bold mb-0 text-dark">
                      {{ metrics.nonActiveEMR }}
                      <span class="metric-pct font-weight-normal text-muted" style="font-size: 13px;">
                        ({{ metrics.nonActiveEMRPct }}%)
                      </span>
                    </h3>
                  </div>
                  <div class="metric-icon-box bg-orange-light text-orange">
                    <b-icon icon="hourglass-split" font-scale="1.2" />
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Visualizations Grid -->
          <!-- Row 1: EMR Status & Ownership -->
          <b-row class="mb-4">
            <!-- EMR Status (Pie Chart) -->
            <b-col cols="12" md="6" lg="6" class="mb-4">
              <div class="chart-card shadow-sm bg-white p-4 rounded-lg" style="height: 450px; display: flex; flex-direction: column;">
                <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h4 class="chart-title text-dark font-weight-bold mb-0 border-bottom-0 pb-0" style="font-size: 14px;">
                    EMR System Status{{ filters.state === 'ALL' ? '' : ` in ${filters.state}` }}
                  </h4>
                  <b-icon icon="fullscreen" class="cursor-pointer text-muted hover-teal" @click="expandChart('status', 'EMR System Status')" v-b-tooltip.hover title="Expand Chart" />
                </div>
                <div id="status-chart" class="emr-chart-container" style="flex: 1;"></div>
              </div>
            </b-col>

            <!-- Ownership Distribution (Column Chart) -->
            <b-col cols="12" md="6" lg="6" class="mb-4">
              <div class="chart-card shadow-sm bg-white p-4 rounded-lg" style="height: 450px; display: flex; flex-direction: column;">
                <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h4 class="chart-title text-dark font-weight-bold mb-0 border-bottom-0 pb-0" style="font-size: 14px;">
                    Ownership Distribution{{ filters.state === 'ALL' ? '' : ` in ${filters.state}` }}
                  </h4>
                  <b-icon icon="fullscreen" class="cursor-pointer text-muted hover-teal" @click="expandChart('ownership', 'Ownership Distribution')" v-b-tooltip.hover title="Expand Chart" />
                </div>
                <div id="ownership-chart" class="emr-chart-container" style="flex: 1;"></div>
              </div>
            </b-col>
          </b-row>

          <!-- Row 2: States/LGAs & BHCPF Status -->
          <b-row class="mb-4">
            <!-- Facilities by State (Bar Chart) -->
            <b-col cols="12" md="6" lg="6" class="mb-4">
              <div class="chart-card shadow-sm bg-white p-4 rounded-lg" style="height: 450px; display: flex; flex-direction: column;">
                <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h4 class="chart-title text-dark font-weight-bold mb-0 border-bottom-0 pb-0" style="font-size: 14px;">
                    {{ filters.state === 'ALL' ? 'Facilities by State' : `Facilities by LGA in ${filters.state}` }}
                  </h4>
                  <b-icon
                    icon="fullscreen"
                    class="cursor-pointer text-muted hover-teal"
                    :class="{ 'flashing-icon': isBarChartScrollable }"
                    @click="expandChart('state', filters.state === 'ALL' ? 'Facilities by State' : `Facilities by LGA in ${filters.state}`)"
                    v-b-tooltip.hover
                    title="Expand Chart"
                  />
                </div>
                <div class="emr-chart-scroll-wrapper" style="flex: 1; overflow-y: auto; padding-right: 5px;">
                  <div id="state-chart"></div>
                </div>
              </div>
            </b-col>

            <!-- BHCPF Status (Bar Chart) -->
            <b-col cols="12" md="6" lg="6" class="mb-4">
              <div class="chart-card shadow-sm bg-white p-4 rounded-lg" style="height: 450px; display: flex; flex-direction: column;">
                <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h4 class="chart-title text-dark font-weight-bold mb-0 border-bottom-0 pb-0" style="font-size: 14px;">
                    BHCPF Facility Status{{ filters.state === 'ALL' ? '' : ` in ${filters.state}` }}
                  </h4>
                  <b-icon icon="fullscreen" class="cursor-pointer text-muted hover-teal" @click="expandChart('bhcpf', 'BHCPF Facility Status')" v-b-tooltip.hover title="Expand Chart" />
                </div>
                <div id="bhcpf-chart" class="emr-chart-container" style="flex: 1;"></div>
              </div>
            </b-col>
          </b-row>
          <!-- Interactive Data Table -->
          <b-row class="mb-5">
            <b-col cols="12">
              <div class="table-card shadow-sm bg-white rounded-lg overflow-hidden border">

                <!-- Table Header -->
                <div class="p-3 border-bottom bg-light d-flex justify-content-between align-items-center flex-wrap">
                  <h3 class="table-card-title text-teal font-weight-bold mb-0">
                    <b-icon icon="list-ul" class="mr-2" />EMR Facilities Directory
                  </h3>
                  <span class="badge badge-pill badge-teal text-white py-2 px-3 font-weight-semibold" style="font-size: 12px; background-color: #007d53;">
                    {{ filteredFacilities.length }} Facilities
                  </span>
                </div>

                <!-- Data Table Element -->
                <div class="table-responsive">
                  <b-table
                    :items="filteredFacilities"
                    :fields="tableFields"
                    :per-page="perPage"
                    :current-page="currentPage"
                    show-empty
                    empty-text="No facilities found matching your search filters."
                    class="mb-0 custom-emr-table"
                    striped
                    hover
                  >
                    <!-- Custom template for EMR ID -->
                    <template #cell(emr_id)="data">
                      <code class="emr-id-badge font-weight-bold">{{ data.value || 'N/A' }}</code>
                    </template>

                    <!-- Custom template for Facility Name -->
                    <template #cell(facility_name)="data">
                      <span class="text-dark font-weight-bold">{{ data.value }}</span>
                    </template>

                    <!-- Custom template for State & LGA -->
                    <template #cell(location)="data">
                      <span>{{ data.item.state }} <span class="text-muted" style="font-size: 16px;">({{ data.item.lga }})</span></span>
                    </template>

                    <!-- Custom template for Level Of Care -->
                    <template #cell(level_of_care)="data">
                      <span class="text-dark font-weight-semibold" style="font-size: 16px; color: #1e293b !important;">{{ data.value || 'Not specified' }}</span>
                    </template>

                    <!-- Custom template for BHCPF Status -->
                    <template #cell(bhcpf_facility)="data">
                      <span
                        class="badge px-2 py-1"
                        :class="{
                          'badge-success': (data.value || '').toLowerCase() === 'yes',
                          'badge-danger': (data.value || '').toLowerCase() === 'no',
                          'badge-light text-muted': (data.value || '').toLowerCase() === 'not specified' || !data.value
                        }"
                        style="font-size: 16px; border-radius: 4px;"
                      >
                        {{ data.value || 'Not specified' }}
                      </span>
                    </template>

                    <!-- Custom template for EMR Status -->
                    <template #cell(emr_status)="data">
                      <span :class="getStatusBadgeClass(data.value)" class="status-badge">
                        {{ data.value || 'Unknown' }}
                      </span>
                    </template>

                    <!-- Custom template for Assignee details -->
                    <template #cell(assignee)="data">
                      <div v-if="data.item.emr_assignee_name">
                        <span class="d-block font-weight-semibold text-dark">{{ data.item.emr_assignee_name }}</span>
                        <span class="d-block text-muted" style="font-size: 16px;">{{ data.item.assignee_designation || 'N/A' }}</span>
                      </div>
                      <span class="text-muted" v-else>Unassigned</span>
                    </template>
                  </b-table>
                </div>

                <!-- Table Pagination -->
                <div class="p-3 border-top d-flex justify-content-between align-items-center flex-wrap bg-light">
                  <div class="text-muted mb-2 mb-sm-0" style="font-size: 12px;">
                    Showing {{ paginationStart }} to {{ paginationEnd }} of {{ filteredFacilities.length }} entries
                  </div>
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="filteredFacilities.length"
                    :per-page="perPage"
                    align="right"
                    size="sm"
                    class="my-0 custom-pagination"
                  />
                </div>

              </div>
            </b-col>
          </b-row>
        </template>
      </b-container>
    </main>

    <!-- Chart Expand Modal -->
    <b-modal id="chart-expand-modal" size="xl" hide-footer centered @shown="onModalShown" @hidden="onModalHidden">
      <template #modal-title>
        <h4 class="modal-title font-weight-bold text-teal mb-0">
          {{ expandedChartTitle }}
        </h4>
      </template>
      <div class="p-4">
        <div id="expanded-chart-container" style="width: 100%; min-height: 480px; margin: 0 auto;"></div>
      </div>
    </b-modal>

    <!-- Footer component -->
    <the-footer :custom-date="lastUpdatedDate" />
  </div>
</template>

<script>
import moment from 'moment';
import Highcharts from 'highcharts';
import exportingInit from 'highcharts/modules/exporting';
import theHeader from '../../about/layout/theHeader.vue';
import theFooter from '../../about/layout/theFooter.vue';

exportingInit(Highcharts);

// High-fidelity Nigerian EMR Facilities Dataset for demo and error fallbacks
const MOCK_EMR_DATA = [
  {
    state: 'Lagos', lga: 'Ikeja', facility_name: 'Ikeja General Hospital', ownership: 'Public', emr_assignee_name: 'Dr. Tunde Alabi', assignee_designation: 'Clinical Informatics Lead', emr_status: 'Active', emr_id: 'EMR-LOS-001', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Lagos', lga: 'Alimosho', facility_name: 'Alimosho General Hospital', ownership: 'Public', emr_assignee_name: 'Sarah Peterson', assignee_designation: 'Senior Systems Analyst', emr_status: 'Active', emr_id: 'EMR-LOS-002', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Lagos', lga: 'Lagos Island', facility_name: 'St. Nicholas Hospital', ownership: 'Private', emr_assignee_name: 'Engr. Emeka Okafor', assignee_designation: 'IT Administrator', emr_status: 'Active', emr_id: 'EMR-LOS-003', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'Lagos', lga: 'Lekki', facility_name: 'Evercare Hospital Lekki', ownership: 'Private', emr_assignee_name: 'Amara Nwosu', assignee_designation: 'Systems Director', emr_status: 'Active', emr_id: 'EMR-LOS-004', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'FCT', lga: 'Municipal', facility_name: 'National Hospital Abuja', ownership: 'Public', emr_assignee_name: 'Dr. Amina Bello', assignee_designation: 'Director of ICT', emr_status: 'Active', emr_id: 'EMR-FCT-001', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'FCT', lga: 'Gwagwalada', facility_name: 'Gwagwalada Specialist Hospital', ownership: 'Public', emr_assignee_name: 'Haruna Musa', assignee_designation: 'Database Manager', emr_status: 'In Progress', emr_id: 'EMR-FCT-002', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'FCT', lga: 'Municipal', facility_name: 'Crestview Medical Center', ownership: 'Private', emr_assignee_name: 'Jessica Adams', assignee_designation: 'Healthcare IT Consultant', emr_status: 'Active', emr_id: 'EMR-FCT-003', level_of_care: 'Primary', bhcpf_facility: 'No',
  },
  {
    state: 'Kano', lga: 'Fagge', facility_name: 'Fagge General Hospital', ownership: 'Public', emr_assignee_name: 'Dr. Ibrahim Yusuf', assignee_designation: 'EMR Specialist', emr_status: 'In Progress', emr_id: 'EMR-KAN-001', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Kano', lga: 'Nassarawa', facility_name: 'Sir Muhammad Sunusi Specialist Hospital', ownership: 'Public', emr_assignee_name: 'Bashir Ahmed', assignee_designation: 'Systems Administrator', emr_status: 'Pending', emr_id: 'EMR-KAN-002', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Kano', lga: 'Tarauni', facility_name: 'Aminu Kano Teaching Hospital', ownership: 'Public', emr_assignee_name: 'Dr. Kabir Sani', assignee_designation: 'Informatics Coordinator', emr_status: 'Active', emr_id: 'EMR-KAN-003', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'Kaduna', lga: 'Kaduna North', facility_name: 'Barau Dikko Teaching Hospital', ownership: 'Public', emr_assignee_name: 'Grace John', assignee_designation: 'ICT Support Officer', emr_status: 'Active', emr_id: 'EMR-KAD-001', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Kaduna', lga: 'Zaria', facility_name: 'Ahmadu Bello University Teaching Hospital', ownership: 'Public', emr_assignee_name: 'Prof. Usman Danfodio', assignee_designation: 'Chief Medical Information Officer', emr_status: 'Active', emr_id: 'EMR-KAD-002', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'Oyo', lga: 'Ibadan North', facility_name: 'University College Hospital (UCH)', ownership: 'Public', emr_assignee_name: 'Dr. Funmi Adebayo', assignee_designation: 'E-Health Coordinator', emr_status: 'Active', emr_id: 'EMR-OYO-001', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'Oyo', lga: 'Ibadan Southwest', facility_name: 'St. Mary\'s Catholic Hospital', ownership: 'Faith-Based', emr_assignee_name: 'Sister Clara Ndubisi', assignee_designation: 'Hospital Administrator', emr_status: 'Active', emr_id: 'EMR-OYO-002', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Oyo', lga: 'Ibadan North', facility_name: 'Alafia Hospital', ownership: 'Private', emr_assignee_name: 'Samuel Ojo', assignee_designation: 'ICT Lead', emr_status: 'Suspended', emr_id: 'EMR-OYO-003', level_of_care: 'Primary', bhcpf_facility: 'No',
  },
  {
    state: 'Enugu', lga: 'Enugu North', facility_name: 'Parklane Teaching Hospital', ownership: 'Public', emr_assignee_name: 'Chinedu Eze', assignee_designation: 'Network Engineer', emr_status: 'In Progress', emr_id: 'EMR-ENU-001', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Enugu', lga: 'Enugu South', facility_name: 'Mother of Mercy Hospital', ownership: 'Faith-Based', emr_assignee_name: 'Rev. Fr. Jude Obinna', assignee_designation: 'IT Director', emr_status: 'Pending', emr_id: 'EMR-ENU-002', level_of_care: 'Primary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Rivers', lga: 'Obio-Akpor', facility_name: 'UPTH Port Harcourt', ownership: 'Public', emr_assignee_name: 'Dr. Tariye Briggs', assignee_designation: 'Clinical Systems Lead', emr_status: 'Active', emr_id: 'EMR-RIV-001', level_of_care: 'Tertiary', bhcpf_facility: 'No',
  },
  {
    state: 'Rivers', lga: 'Port Harcourt', facility_name: 'Braithwaite Memorial Specialist Hospital', ownership: 'Public', emr_assignee_name: 'Tonye Cole', assignee_designation: 'Systems Administrator', emr_status: 'Active', emr_id: 'EMR-RIV-002', level_of_care: 'Secondary', bhcpf_facility: 'Yes',
  },
  {
    state: 'Rivers', lga: 'Port Harcourt', facility_name: 'Port Harcourt Medical Center', ownership: 'Private', emr_assignee_name: 'Nene George', assignee_designation: 'Database Manager', emr_status: 'Pending', emr_id: 'EMR-RIV-003', level_of_care: 'Primary', bhcpf_facility: 'No',
  },
];

const LOCAL_STORAGE_URL_KEY = 'msdat-emr-google-sheet-url';

export default {
  name: 'EmrFacilitiesDashboard',
  components: {
    theHeader,
    theFooter,
  },
  data() {
    return {
      loading: false,
      error: '',
      googleSheetUrl: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQXolbqtED5RGdLXH2AGTZau8ZgR9UhozuY9tZfRFhJNlNf7kGQCmt5kRkEHNmBsClTdMFVdIGT32p_/pub?gid=0&single=true&output=csv',
      isUsingMockData: false,
      rawFacilities: [],
      lastUpdatedDate: '',
      expandedChartType: '',
      expandedChartTitle: '',
      expandedChart: null,
      isBarChartScrollable: false,
      layerStatus: false,
      metrics: {
        totalFacilities: 0,
        totalStates: 0,
        activeEMR: 0,
        activeEMRPct: 0,
        nonActiveEMR: 0,
        nonActiveEMRPct: 0,
      },
      filters: {
        state: 'ALL',
        lga: 'ALL',
        ownership: 'ALL',
        status: 'ALL',
        bhcpf: 'ALL',
        levelOfCare: 'ALL',
        search: '',
      },
      // Table configuration
      perPage: 10,
      currentPage: 1,
      tableFields: [
        { key: 'emr_id', label: 'EMR ID', sortable: true },
        { key: 'facility_name', label: 'Facility Name', sortable: true },
        { key: 'location', label: 'State & LGA', sortable: true },
        { key: 'level_of_care', label: 'Level of Care', sortable: true },
        { key: 'ownership', label: 'Ownership', sortable: true },
        { key: 'bhcpf_facility', label: 'BHCPF Status', sortable: true },
        { key: 'emr_status', label: 'EMR Status', sortable: true },
        { key: 'assignee', label: 'EMR Assignee', sortable: false },
      ],
      // Instantiated charts to prevent memory leaks
      charts: {
        status: null,
        ownership: null,
        state: null,
        bhcpf: null,
      },
    };
  },
  computed: {
    // Dynamic Filter Option Lists
    stateOptions() {
      const states = this.rawFacilities
        .map((f) => f.state)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .sort();
      return [{ value: 'ALL', text: 'All States' }, ...states.map((s) => ({ value: s, text: s }))];
    },
    lgaOptions() {
      let queryFacilities = this.rawFacilities;
      if (this.filters.state !== 'ALL') {
        queryFacilities = queryFacilities.filter((f) => f.state === this.filters.state);
      }
      const lgas = queryFacilities
        .map((f) => f.lga)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .sort();
      return [{ value: 'ALL', text: 'All LGAs' }, ...lgas.map((l) => ({ value: l, text: l }))];
    },
    ownershipOptions() {
      const types = this.rawFacilities
        .map((f) => f.ownership)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .sort();
      return [{ value: 'ALL', text: 'All Ownerships' }, ...types.map((t) => ({ value: t, text: t }))];
    },
    statusOptions() {
      const statuses = this.rawFacilities
        .map((f) => f.emr_status)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .sort();
      return [{ value: 'ALL', text: 'All Statuses' }, ...statuses.map((s) => ({ value: s, text: s }))];
    },
    bhcpfOptions() {
      const options = this.rawFacilities
        .map((f) => f.bhcpf_facility || 'Not specified')
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
      return [{ value: 'ALL', text: 'All BHCPF Statuses' }, ...options.map((o) => ({ value: o, text: o }))];
    },
    levelOfCareOptions() {
      const options = this.rawFacilities
        .map((f) => f.level_of_care || 'Not specified')
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
      return [{ value: 'ALL', text: 'All Levels of Care' }, ...options.map((o) => ({ value: o, text: o }))];
    },
    // Filtered Dataset
    filteredFacilities() {
      return this.rawFacilities.filter((facility) => {
        // State Filter
        if (this.filters.state !== 'ALL' && facility.state !== this.filters.state) {
          return false;
        }
        // LGA Filter
        if (this.filters.lga !== 'ALL' && facility.lga !== this.filters.lga) {
          return false;
        }
        // Level of Care Filter
        if (this.filters.levelOfCare !== 'ALL') {
          const val = facility.level_of_care || 'Not specified';
          if (val !== this.filters.levelOfCare) return false;
        }
        // Ownership Filter
        if (this.filters.ownership !== 'ALL' && facility.ownership !== this.filters.ownership) {
          return false;
        }
        // BHCPF Facility Filter
        if (this.filters.bhcpf !== 'ALL') {
          const val = facility.bhcpf_facility || 'Not specified';
          if (val !== this.filters.bhcpf) return false;
        }
        // Status Filter
        if (this.filters.status !== 'ALL' && facility.emr_status !== this.filters.status) {
          return false;
        }
        // General Search Filter
        if (this.filters.search) {
          const searchVal = this.filters.search.toLowerCase();
          const matchName = facility.facility_name?.toLowerCase().includes(searchVal);
          const matchAssignee = facility.emr_assignee_name?.toLowerCase().includes(searchVal);
          const matchDesignation = facility.assignee_designation?.toLowerCase().includes(searchVal);
          const matchId = facility.emr_id?.toLowerCase().includes(searchVal);
          const matchLga = facility.lga?.toLowerCase().includes(searchVal);
          const matchState = facility.state?.toLowerCase().includes(searchVal);

          return matchName || matchAssignee || matchDesignation || matchId || matchLga || matchState;
        }
        return true;
      });
    },
    // Pagination helpers
    paginationStart() {
      if (this.filteredFacilities.length === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      const end = this.currentPage * this.perPage;
      return end > this.filteredFacilities.length ? this.filteredFacilities.length : end;
    },
  },
  watch: {
    // Re-render charts reactively when filters change
    filteredFacilities: {
      handler() {
        this.calculateMetrics();
        this.$nextTick(() => {
          this.renderCharts();
        });
      },
      deep: true,
    },
    layerStatus() {
      this.$nextTick(() => {
        this.renderCharts();
      });
    },
    // Reset LGA when State changes
    'filters.state': function filtersState() {
      this.filters.lga = 'ALL';
    },
  },
  created() {
    // Restore spreadsheet URL config from localStorage
    const savedUrl = localStorage.getItem(LOCAL_STORAGE_URL_KEY);
    if (savedUrl) {
      this.googleSheetUrl = savedUrl;
      this.isUsingMockData = false;
    }
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    // Destroy charts to prevent memory leaks
    Object.keys(this.charts).forEach((key) => {
      if (this.charts[key]) {
        this.charts[key].destroy();
        this.charts[key] = null;
      }
    });
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = '';

      if (!this.googleSheetUrl) {
        // No URL provided, fall back to mock data immediately
        this.rawFacilities = MOCK_EMR_DATA.map((item) => ({
          level_of_care: 'Primary',
          bhcpf_facility: 'No',
          ...item,
        }));
        this.isUsingMockData = true;
        this.calculateMetrics();
        this.$nextTick(() => {
          this.renderCharts();
        });
        this.loading = false;
        return;
      }

      try {
        // Prevent caching of spreadsheet fetch
        const cacheBuster = `&t=${new Date().getTime()}`;
        const targetUrl = this.googleSheetUrl.includes('?')
          ? `${this.googleSheetUrl}${cacheBuster}`
          : `${this.googleSheetUrl}?${cacheBuster}`;

        const response = await fetch(targetUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const csvText = await response.text();
        if (csvText) {
          const trimmedText = csvText.trim();
          if (trimmedText.toLowerCase().startsWith('<!doctype') || trimmedText.toLowerCase().startsWith('<html') || trimmedText.toLowerCase().startsWith('<head')) {
            throw new Error('The spreadsheet URL returned an HTML page instead of a CSV. Please verify that the Google Sheet is published to the web as CSV and shared publicly.');
          }
          const parsed = this.processCSVData(trimmedText);
          if (parsed && parsed.length > 0) {
            this.rawFacilities = parsed;
            this.isUsingMockData = false;
            this.lastUpdatedDate = moment().format('MMMM Do YYYY');
          } else {
            throw new Error('Spreadsheet parsed successfully but returned 0 valid rows.');
          }
        } else {
          throw new Error('Empty spreadsheet response.');
        }
      } catch (err) {
        console.error('Error fetching Google Sheets EMR data:', err);
        this.error = err.message || 'Network error fetching the sheet.';
        // Fail-safe: load mock data anyway
        this.rawFacilities = MOCK_EMR_DATA.map((item) => ({
          level_of_care: 'Primary',
          bhcpf_facility: 'No',
          ...item,
        }));
        this.isUsingMockData = true;
      } finally {
        this.calculateMetrics();
        this.$nextTick(() => {
          this.renderCharts();
        });
        this.loading = false;
      }
    },
    saveAndLoadUrl() {
      if (this.googleSheetUrl) {
        localStorage.setItem(LOCAL_STORAGE_URL_KEY, this.googleSheetUrl);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_URL_KEY);
      }
      this.fetchData();
    },
    // Double-quote and separator aware CSV string parser
    parseCSV(text) {
      if (!text) return [];
      const lines = [];
      let row = [''];
      let inQuotes = false;

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const nextChar = text[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            row[row.length - 1] += '"';
            i++; // skip escaped quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          row.push('');
        } else if ((char === '\r' || char === '\n') && !inQuotes) {
          if (char === '\r' && nextChar === '\n') {
            i++; // handle CRLF
          }
          lines.push(row);
          row = [''];
        } else {
          row[row.length - 1] += char;
        }
      }
      if (row.length > 1 || row[0] !== '') {
        lines.push(row);
      }
      return lines;
    },
    // Map CSV columns to standardized schema
    processCSVData(csvText) {
      const parsed = this.parseCSV(csvText.trim());
      if (parsed.length < 2) return [];

      const headers = parsed[0].map((h) => h.trim().toLowerCase());
      const data = [];

      for (let i = 1; i < parsed.length; i++) {
        const row = parsed[i];
        if (row.length >= headers.length) {
          const obj = {};
          headers.forEach((header, index) => {
            const val = row[index] ? row[index].trim() : '';
            const h = header.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

            // Map index values to match schema exactly, preventing column collisions
            if (h === 'state') obj.state = val;
            else if (h === 'lga') obj.lga = val;
            else if (h === 'facility name') obj.facility_name = val;
            else if (h === 'ownership') obj.ownership = val;
            else if (h === 'level of care') obj.level_of_care = val;
            else if (h === 'bhcpf facility') obj.bhcpf_facility = val;
            else if (h === 'emr assignee name') obj.emr_assignee_name = val;
            else if (h === 'assignee designation') obj.assignee_designation = val;
            else if (h === 'emr status') obj.emr_status = val;
            else if (h === 'emr id') obj.emr_id = val;
            else obj[header] = val; // fallback for exact headers
          });

          // Ensure keys exist in final structure
          const item = {
            state: obj.state || '',
            lga: obj.lga || '',
            facility_name: obj.facility_name || '',
            level_of_care: obj.level_of_care || '',
            ownership: obj.ownership || '',
            bhcpf_facility: obj.bhcpf_facility || '',
            emr_assignee_name: obj.emr_assignee_name || '',
            assignee_designation: obj.assignee_designation || '',
            emr_status: obj.emr_status || '',
            emr_id: obj.emr_id || '',
          };

          if (item.facility_name) {
            data.push(item);
          }
        }
      }
      return data;
    },
    // Calculate global stats for current filter selection
    calculateMetrics() {
      const data = this.filteredFacilities;
      const total = data.length;

      const uniqueStates = data.map((f) => f.state).filter((v, i, a) => v && a.indexOf(v) === i).length;

      const activeCount = data.filter((f) => {
        const s = (f.emr_status || '').toLowerCase();
        return s === 'active' || s === 'fully functional' || s === 'implemented';
      }).length;
      const nonActiveCount = total - activeCount;

      this.metrics = {
        totalFacilities: total,
        totalStates: uniqueStates,
        activeEMR: activeCount,
        activeEMRPct: total > 0 ? Math.round((activeCount / total) * 100) : 0,
        nonActiveEMR: nonActiveCount,
        nonActiveEMRPct: total > 0 ? Math.round((nonActiveCount / total) * 100) : 0,
      };
    },
    // Render and update Highcharts instances
    renderCharts() {
      const data = this.filteredFacilities;
      if (!data || data.length === 0) {
        // Destroy existing if data empty
        Object.keys(this.charts).forEach((key) => {
          if (this.charts[key]) {
            this.charts[key].destroy();
            this.charts[key] = null;
          }
        });
        return;
      }

      // Check if elements exist in DOM
      const hasStatusEl = document.getElementById('status-chart');
      const hasOwnershipEl = document.getElementById('ownership-chart');
      const hasStateEl = document.getElementById('state-chart');
      const hasBhcpfEl = document.getElementById('bhcpf-chart');

      if (!hasStatusEl || !hasOwnershipEl || !hasStateEl || !hasBhcpfEl) return;

      // 1. Status Chart Data Processing
      const statusMap = {};
      data.forEach((f) => {
        const s = f.emr_status || 'Unknown';
        statusMap[s] = (statusMap[s] || 0) + 1;
      });
      const statusSeries = Object.keys(statusMap).map((k) => ({
        name: k,
        y: statusMap[k],
      }));

      // Define standard palette
      const colors = ['#007d53', '#17b7b5', '#f8bc16', '#ff4d4f', '#6c757d'];

      // Draw/Update Status Donut Chart
      this.charts.status = Highcharts.chart('status-chart', {
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          height: 350,
          spacing: [10, 10, 10, 10],
        },
        title: { text: null },
        tooltip: {
          pointFormat: '<b>{point.y}</b> facilities ({point.percentage:.1f}%)',
        },
        accessibility: { enabled: false },
        plotOptions: {
          pie: {
            innerSize: '60%',
            allowPointSelect: true,
            cursor: 'pointer',
            colors,
            dataLabels: {
              enabled: true,
              format: '{point.name}: {point.y}',
              style: {
                fontSize: '10px',
                fontFamily: 'Work Sans, sans-serif',
              },
              distance: 10,
            },
          },
        },
        series: [{
          name: 'Status',
          colorByPoint: true,
          data: statusSeries,
        }],
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
            },
          },
          chartOptions: {
            chart: {
              backgroundColor: '#ffffff',
            },
            title: {
              text: this.filters.state === 'ALL' ? 'EMR System Status' : `EMR System Status in ${this.filters.state}`,
              style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
            },
          },
        },
        credits: { enabled: false },
      });

      // 2. Ownership Distribution Bar Chart
      const ownMap = {};
      data.forEach((f) => {
        const o = f.ownership || 'Unknown';
        ownMap[o] = (ownMap[o] || 0) + 1;
      });
      const ownCategories = Object.keys(ownMap).sort();

      let ownSeries = [];
      let ownPlotOptions = {
        column: {
          color: '#17b7b5',
          borderRadius: 4,
          dataLabels: { enabled: true, format: '{y}' },
        },
      };
      let ownLegend = { enabled: false };

      if (this.layerStatus) {
        const uniqueStatuses = [...new Set(data.map((f) => f.emr_status || 'Unknown'))].sort((a, b) => {
          const priority = {
            active: 1, 'fully functional': 1, implemented: 1, 'in progress': 2, 'in-progress': 2, 'partially functional': 2, pending: 3, 'yet to commence': 3, suspended: 4,
          };
          const pa = priority[a.toLowerCase()] || 99;
          const pb = priority[b.toLowerCase()] || 99;
          return pa - pb;
        });

        ownSeries = uniqueStatuses.map((status) => {
          const counts = ownCategories.map((cat) => data.filter((f) => (f.ownership || 'Unknown') === cat && (f.emr_status || 'Unknown') === status).length);
          return {
            name: status,
            data: counts,
            color: this.getStatusColor(status),
          };
        });

        ownPlotOptions = {
          column: {
            stacking: 'normal',
            borderRadius: 4,
            dataLabels: { enabled: true, format: '{y}', style: { textOutline: 'none' } },
          },
        };
        ownLegend = {
          enabled: true,
          itemStyle: { fontFamily: 'Work Sans, sans-serif', fontSize: '10px' },
        };
      } else {
        const ownData = ownCategories.map((cat) => ownMap[cat]);
        ownSeries = [{
          name: 'Facilities',
          data: ownData,
        }];
      }

      this.charts.ownership = Highcharts.chart('ownership-chart', {
        chart: {
          type: 'column',
          backgroundColor: 'transparent',
          height: 350,
        },
        title: { text: null },
        xAxis: {
          categories: ownCategories,
          labels: {
            style: {
              fontSize: '10px',
              fontFamily: 'Work Sans, sans-serif',
            },
          },
        },
        yAxis: {
          min: 0,
          title: { text: 'Facilities Count' },
          gridLineColor: '#f3f4f6',
        },
        tooltip: {
          headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
          pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>'
            + '<td style="padding:0"><b>{point.y} facilities</b></td></tr>',
          footerFormat: '</table>',
          shared: true,
          useHTML: true,
        },
        accessibility: { enabled: false },
        legend: ownLegend,
        plotOptions: ownPlotOptions,
        series: ownSeries,
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
            },
          },
          chartOptions: {
            chart: {
              backgroundColor: '#ffffff',
            },
            title: {
              text: this.filters.state === 'ALL' ? 'Ownership Distribution' : `Ownership Distribution in ${this.filters.state}`,
              style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
            },
          },
        },
        credits: { enabled: false },
      });

      // 3. States/LGAs Bar Chart (Horizontal with Drill-down)
      const stateMap = {};
      const isStateSelected = this.filters.state !== 'ALL';
      const vm = this;

      if (!isStateSelected) {
        // Aggregate by State
        data.forEach((f) => {
          const s = f.state || 'Unknown';
          stateMap[s] = (stateMap[s] || 0) + 1;
        });
      } else {
        // Aggregate by LGA for the selected State
        data.forEach((f) => {
          const l = f.lga || 'Unknown';
          stateMap[l] = (stateMap[l] || 0) + 1;
        });
      }

      const sortedItems = Object.keys(stateMap).map((k) => ({
        name: k,
        count: stateMap[k],
      })).sort((a, b) => b.count - a.count); // removed slice to show all items

      const chartCategories = sortedItems.map((item) => item.name);
      const chartSeriesData = sortedItems.map((item) => item.count);

      const categoriesCount = chartCategories.length;
      const dynamicHeight = Math.max(350, categoriesCount * 40);
      this.isBarChartScrollable = dynamicHeight > 350;

      let stateSeries = [];
      let statePlotOptions = {
        bar: {
          color: isStateSelected ? '#17b7b5' : '#007d53',
          borderRadius: 3,
          cursor: 'pointer',
          pointPadding: 0.1,
          groupPadding: 0.15,
          dataLabels: {
            enabled: true,
            format: '{y}',
            style: {
              fontSize: '9px',
              fontFamily: 'Work Sans, sans-serif',
            },
          },
          point: {
            events: {
              click() {
                const clickedName = this.category;
                if (!isStateSelected) {
                  vm.filters.state = clickedName;
                } else {
                  vm.filters.lga = clickedName;
                }
              },
            },
          },
        },
      };
      let stateLegend = { enabled: false };

      if (this.layerStatus) {
        const uniqueStatuses = [...new Set(data.map((f) => f.emr_status || 'Unknown'))].sort((a, b) => {
          const priority = {
            active: 1, 'fully functional': 1, implemented: 1, 'in progress': 2, 'in-progress': 2, 'partially functional': 2, pending: 3, 'yet to commence': 3, suspended: 4,
          };
          const pa = priority[a.toLowerCase()] || 99;
          const pb = priority[b.toLowerCase()] || 99;
          return pa - pb;
        });

        stateSeries = uniqueStatuses.map((status) => {
          const counts = chartCategories.map((cat) => data.filter((f) => {
            const fCat = isStateSelected ? f.lga : f.state;
            return fCat === cat && (f.emr_status || 'Unknown') === status;
          }).length);
          return {
            name: status,
            data: counts,
            color: this.getStatusColor(status),
          };
        });

        statePlotOptions = {
          bar: {
            stacking: 'normal',
            borderRadius: 3,
            cursor: 'pointer',
            pointPadding: 0.1,
            groupPadding: 0.15,
            dataLabels: {
              enabled: true,
              format: '{y}',
              style: {
                fontSize: '9px',
                fontFamily: 'Work Sans, sans-serif',
                textOutline: 'none',
              },
            },
            point: {
              events: {
                click() {
                  const clickedName = this.category;
                  if (!isStateSelected) {
                    vm.filters.state = clickedName;
                  } else {
                    vm.filters.lga = clickedName;
                  }
                },
              },
            },
          },
        };
        stateLegend = {
          enabled: true,
          itemStyle: { fontFamily: 'Work Sans, sans-serif', fontSize: '10px' },
        };
      } else {
        stateSeries = [{
          name: isStateSelected ? 'LGA Facilities' : 'State Facilities',
          data: chartSeriesData,
        }];
      }

      this.charts.state = Highcharts.chart('state-chart', {
        chart: {
          type: 'bar',
          backgroundColor: 'transparent',
          height: dynamicHeight,
          spacingBottom: 15,
          spacingTop: 15,
          spacingLeft: 10,
          spacingRight: 15,
        },
        title: { text: null },
        xAxis: {
          categories: chartCategories,
          labels: {
            style: {
              fontSize: categoriesCount > 10 ? '9px' : '10px',
              fontFamily: 'Work Sans, sans-serif',
            },
          },
        },
        yAxis: {
          min: 0,
          title: { text: 'Facilities Count' },
          gridLineColor: '#f3f4f6',
        },
        legend: stateLegend,
        tooltip: {
          pointFormat: '<b>{point.y}</b> facilities',
        },
        accessibility: { enabled: false },
        plotOptions: statePlotOptions,
        series: stateSeries,
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
            },
          },
          chartOptions: {
            chart: {
              backgroundColor: '#ffffff',
            },
            title: {
              text: isStateSelected ? `Facilities by LGA in ${this.filters.state}` : 'Facilities by State',
              style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
            },
          },
        },
        credits: { enabled: false },
      });

      // 4. BHCPF Facility Status Bar Chart
      const bhcpfMap = {};
      data.forEach((f) => {
        const b = f.bhcpf_facility || 'Not specified';
        bhcpfMap[b] = (bhcpfMap[b] || 0) + 1;
      });

      // Sort categories: Yes, No, then others
      const bhcpfCategories = Object.keys(bhcpfMap).sort((a, b) => {
        if (a === 'Yes') return -1;
        if (b === 'Yes') return 1;
        if (a === 'No') return -1;
        if (b === 'No') return 1;
        return a.localeCompare(b);
      });
      const bhcpfData = bhcpfCategories.map((cat) => bhcpfMap[cat]);

      let bhcpfSeries = [];
      let bhcpfPlotOptions = {
        bar: {
          color: '#f8bc16', // Gold/amber color for BHCPF
          borderRadius: 3,
          dataLabels: {
            enabled: true,
            format: '{y}',
            style: {
              fontSize: '9px',
              fontFamily: 'Work Sans, sans-serif',
            },
          },
        },
      };
      let bhcpfLegend = { enabled: false };

      if (this.layerStatus) {
        const uniqueStatuses = [...new Set(data.map((f) => f.emr_status || 'Unknown'))].sort((a, b) => {
          const priority = {
            active: 1, 'fully functional': 1, implemented: 1, 'in progress': 2, 'in-progress': 2, 'partially functional': 2, pending: 3, 'yet to commence': 3, suspended: 4,
          };
          const pa = priority[a.toLowerCase()] || 99;
          const pb = priority[b.toLowerCase()] || 99;
          return pa - pb;
        });

        bhcpfSeries = uniqueStatuses.map((status) => {
          const counts = bhcpfCategories.map((cat) => data.filter((f) => (f.bhcpf_facility || 'Not specified') === cat && (f.emr_status || 'Unknown') === status).length);
          return {
            name: status,
            data: counts,
            color: this.getStatusColor(status),
          };
        });

        bhcpfPlotOptions = {
          bar: {
            stacking: 'normal',
            borderRadius: 3,
            dataLabels: {
              enabled: true,
              format: '{y}',
              style: {
                fontSize: '9px',
                fontFamily: 'Work Sans, sans-serif',
                textOutline: 'none',
              },
            },
          },
        };
        bhcpfLegend = {
          enabled: true,
          itemStyle: { fontFamily: 'Work Sans, sans-serif', fontSize: '10px' },
        };
      } else {
        bhcpfSeries = [{
          name: 'Facilities',
          data: bhcpfData,
        }];
      }

      this.charts.bhcpf = Highcharts.chart('bhcpf-chart', {
        chart: {
          type: 'bar',
          backgroundColor: 'transparent',
          height: 350,
          spacingBottom: 15,
          spacingTop: 15,
          spacingLeft: 10,
          spacingRight: 15,
        },
        title: { text: null },
        xAxis: {
          categories: bhcpfCategories,
          labels: {
            style: {
              fontSize: '10px',
              fontFamily: 'Work Sans, sans-serif',
            },
          },
        },
        yAxis: {
          min: 0,
          title: { text: 'Facilities Count' },
          gridLineColor: '#f3f4f6',
        },
        legend: bhcpfLegend,
        tooltip: {
          pointFormat: '<b>{point.y}</b> facilities',
        },
        accessibility: { enabled: false },
        plotOptions: bhcpfPlotOptions,
        series: bhcpfSeries,
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
            },
          },
          chartOptions: {
            chart: {
              backgroundColor: '#ffffff',
            },
            title: {
              text: this.filters.state === 'ALL' ? 'BHCPF Facility Status' : `BHCPF Facility Status in ${this.filters.state}`,
              style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
            },
          },
        },
        credits: { enabled: false },
      });
    },
    // Reset Filters to default
    resetFilters() {
      this.filters = {
        state: 'ALL',
        lga: 'ALL',
        ownership: 'ALL',
        status: 'ALL',
        bhcpf: 'ALL',
        levelOfCare: 'ALL',
        search: '',
      };
      this.layerStatus = false;
      this.currentPage = 1;
    },
    // Export currently filtered list to local CSV file
    exportToCSV() {
      const data = this.filteredFacilities;
      if (data.length === 0) return;

      const headers = ['State', 'LGA', 'Facility Name', 'Level Of Care', 'Ownership', 'BHCPF Facility', 'EMR Assignee Name', 'Assignee Designation', 'EMR Status', 'EMR ID'];

      const csvRows = [
        headers.join(','),
        ...data.map((item) => [
          `"${(item.state || '').replace(/"/g, '""')}"`,
          `"${(item.lga || '').replace(/"/g, '""')}"`,
          `"${(item.facility_name || '').replace(/"/g, '""')}"`,
          `"${(item.level_of_care || '').replace(/"/g, '""')}"`,
          `"${(item.ownership || '').replace(/"/g, '""')}"`,
          `"${(item.bhcpf_facility || '').replace(/"/g, '""')}"`,
          `"${(item.emr_assignee_name || '').replace(/"/g, '""')}"`,
          `"${(item.assignee_designation || '').replace(/"/g, '""')}"`,
          `"${(item.emr_status || '').replace(/"/g, '""')}"`,
          `"${(item.emr_id || '').replace(/"/g, '""')}"`,
        ].join(',')),
      ];

      const csvContent = `data:text/csv;charset=utf-8,${csvRows.join('\n')}`;
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `emr_facilities_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // Render dynamic status classes
    getStatusBadgeClass(status) {
      if (!status) return 'status-badge--pending';
      const s = status.toLowerCase();
      if (s === 'active' || s === 'implemented' || s === 'fully functional') return 'status-badge--active';
      if (s === 'in progress' || s === 'in-progress' || s === 'partially functional') return 'status-badge--in-progress';
      if (s === 'suspended') return 'status-badge--suspended';
      return 'status-badge--pending';
    },
    getStatusColor(status) {
      const s = (status || '').toLowerCase();
      if (s === 'active' || s === 'implemented' || s === 'fully functional') return '#007d53';
      if (s === 'in progress' || s === 'in-progress' || s === 'partially functional') return '#17b7b5';
      if (s === 'suspended') return '#ff4d4f';
      if (s === 'pending' || s === 'yet to commence') return '#f8bc16';
      return '#6c757d';
    },
    expandChart(type, baseTitle) {
      this.expandedChartType = type;
      let title = baseTitle;
      if (type === 'state') {
        title = this.filters.state === 'ALL' ? 'Facilities by State' : `Facilities by LGA in ${this.filters.state}`;
      } else {
        const stateSuffix = this.filters.state === 'ALL' ? '' : ` in ${this.filters.state}`;
        title = `${baseTitle}${stateSuffix}`;
      }
      this.expandedChartTitle = title;
      this.$bvModal.show('chart-expand-modal');
    },
    onModalShown() {
      const data = this.filteredFacilities;
      if (!data || data.length === 0) return;

      const containerId = 'expanded-chart-container';

      if (this.expandedChartType === 'status') {
        const statusMap = {};
        data.forEach((f) => {
          const s = f.emr_status || 'Unknown';
          statusMap[s] = (statusMap[s] || 0) + 1;
        });
        const statusSeries = Object.keys(statusMap).map((k) => ({
          name: k,
          y: statusMap[k],
        }));

        const colors = ['#007d53', '#17b7b5', '#f8bc16', '#ff4d4f', '#6c757d'];

        this.expandedChart = Highcharts.chart(containerId, {
          chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 480,
          },
          title: {
            text: this.expandedChartTitle,
            style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
          },
          tooltip: { pointFormat: '{series.name}: <b>{point.y} ({point.percentage:.1f}%)</b>' },
          accessibility: { enabled: false },
          plotOptions: {
            pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              colors,
              dataLabels: {
                enabled: true,
                format: '<b>{point.name}</b>: {point.y} ({point.percentage:.1f}%)',
                style: { fontFamily: 'Work Sans, sans-serif', fontSize: '12px' },
              },
            },
          },
          series: [{
            name: 'Facilities',
            colorByPoint: true,
            innerSize: '50%',
            data: statusSeries,
          }],
          exporting: {
            enabled: true,
            buttons: {
              contextButton: {
                menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
              },
            },
            chartOptions: {
              chart: {
                backgroundColor: '#ffffff',
              },
            },
          },
          credits: { enabled: false },
        });
      } else if (this.expandedChartType === 'ownership') {
        const ownMap = {};
        data.forEach((f) => {
          const o = f.ownership || 'Unknown';
          ownMap[o] = (ownMap[o] || 0) + 1;
        });
        const sortedOwn = Object.keys(ownMap).map((k) => ({
          name: k,
          count: ownMap[k],
        })).sort((a, b) => b.count - a.count);

        const ownCategories = sortedOwn.map((o) => o.name);

        let ownSeries = [];
        let ownPlotOptions = {
          column: {
            color: '#17b7b5',
            borderRadius: 4,
            dataLabels: { enabled: true, format: '{y}' },
          },
        };
        let ownLegend = { enabled: false };

        if (this.layerStatus) {
          const uniqueStatuses = [...new Set(data.map((f) => f.emr_status || 'Unknown'))].sort((a, b) => {
            const priority = {
              active: 1, 'fully functional': 1, implemented: 1, 'in progress': 2, 'in-progress': 2, 'partially functional': 2, pending: 3, 'yet to commence': 3, suspended: 4,
            };
            const pa = priority[a.toLowerCase()] || 99;
            const pb = priority[b.toLowerCase()] || 99;
            return pa - pb;
          });

          ownSeries = uniqueStatuses.map((status) => {
            const counts = ownCategories.map((cat) => data.filter((f) => (f.ownership || 'Unknown') === cat && (f.emr_status || 'Unknown') === status).length);
            return {
              name: status,
              data: counts,
              color: this.getStatusColor(status),
            };
          });

          ownPlotOptions = {
            column: {
              stacking: 'normal',
              borderRadius: 4,
              dataLabels: { enabled: true, format: '{y}', style: { textOutline: 'none' } },
            },
          };
          ownLegend = {
            enabled: true,
            itemStyle: { fontFamily: 'Work Sans, sans-serif', fontSize: '11px' },
          };
        } else {
          const ownData = sortedOwn.map((o) => o.count);
          ownSeries = [{ name: 'Facilities', data: ownData }];
        }

        this.expandedChart = Highcharts.chart(containerId, {
          chart: {
            type: 'column',
            backgroundColor: 'transparent',
            height: 480,
          },
          title: {
            text: this.expandedChartTitle,
            style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
          },
          xAxis: {
            categories: ownCategories,
            labels: { style: { fontSize: '11px', fontFamily: 'Work Sans, sans-serif' } },
          },
          yAxis: {
            min: 0,
            title: { text: 'Facilities Count' },
          },
          tooltip: { shared: true },
          legend: ownLegend,
          plotOptions: ownPlotOptions,
          series: ownSeries,
          exporting: {
            enabled: true,
            buttons: {
              contextButton: {
                menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
              },
            },
            chartOptions: {
              chart: {
                backgroundColor: '#ffffff',
              },
            },
          },
          credits: { enabled: false },
        });
      } else if (this.expandedChartType === 'state') {
        const stateMap = {};
        const isStateSelected = this.filters.state !== 'ALL';
        if (!isStateSelected) {
          data.forEach((f) => {
            const s = f.state || 'Unknown';
            stateMap[s] = (stateMap[s] || 0) + 1;
          });
        } else {
          data.forEach((f) => {
            const l = f.lga || 'Unknown';
            stateMap[l] = (stateMap[l] || 0) + 1;
          });
        }

        const sortedItems = Object.keys(stateMap).map((k) => ({
          name: k,
          count: stateMap[k],
        })).sort((a, b) => b.count - a.count);

        const chartCategories = sortedItems.map((item) => item.name);

        let stateSeries = [];
        let statePlotOptions = {
          bar: {
            color: isStateSelected ? '#17b7b5' : '#007d53',
            borderRadius: 4,
            dataLabels: { enabled: true, format: '{y}' },
          },
        };
        let stateLegend = { enabled: false };

        if (this.layerStatus) {
          const uniqueStatuses = [...new Set(data.map((f) => f.emr_status || 'Unknown'))].sort((a, b) => {
            const priority = {
              active: 1, 'fully functional': 1, implemented: 1, 'in progress': 2, 'in-progress': 2, 'partially functional': 2, pending: 3, 'yet to commence': 3, suspended: 4,
            };
            const pa = priority[a.toLowerCase()] || 99;
            const pb = priority[b.toLowerCase()] || 99;
            return pa - pb;
          });

          stateSeries = uniqueStatuses.map((status) => {
            const counts = chartCategories.map((cat) => data.filter((f) => {
              const fCat = isStateSelected ? f.lga : f.state;
              return fCat === cat && (f.emr_status || 'Unknown') === status;
            }).length);
            return {
              name: status,
              data: counts,
              color: this.getStatusColor(status),
            };
          });

          statePlotOptions = {
            bar: {
              stacking: 'normal',
              borderRadius: 4,
              dataLabels: { enabled: true, format: '{y}', style: { textOutline: 'none' } },
            },
          };
          stateLegend = {
            enabled: true,
            itemStyle: { fontFamily: 'Work Sans, sans-serif', fontSize: '11px' },
          };
        } else {
          const chartSeriesData = sortedItems.map((item) => item.count);
          stateSeries = [{ name: 'Facilities', data: chartSeriesData }];
        }

        this.expandedChart = Highcharts.chart(containerId, {
          chart: {
            type: 'bar',
            backgroundColor: 'transparent',
            height: Math.max(480, chartCategories.length * 35),
          },
          title: {
            text: this.expandedChartTitle,
            style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
          },
          xAxis: {
            categories: chartCategories,
            labels: { style: { fontSize: '11px', fontFamily: 'Work Sans, sans-serif' } },
          },
          yAxis: {
            min: 0,
            title: { text: 'Facilities Count' },
          },
          legend: stateLegend,
          tooltip: { pointFormat: '<b>{point.y}</b> facilities' },
          plotOptions: statePlotOptions,
          series: stateSeries,
          exporting: {
            enabled: true,
            buttons: {
              contextButton: {
                menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
              },
            },
            chartOptions: {
              chart: {
                backgroundColor: '#ffffff',
              },
            },
          },
          credits: { enabled: false },
        });
      } else if (this.expandedChartType === 'bhcpf') {
        const bhcpfMap = {};
        data.forEach((f) => {
          const b = f.bhcpf_facility || 'Not specified';
          bhcpfMap[b] = (bhcpfMap[b] || 0) + 1;
        });

        // Sort categories: Yes, No, then others
        const bhcpfCategories = Object.keys(bhcpfMap).sort((a, b) => {
          if (a === 'Yes') return -1;
          if (b === 'Yes') return 1;
          if (a === 'No') return -1;
          if (b === 'No') return 1;
          return a.localeCompare(b);
        });

        let bhcpfSeries = [];
        let bhcpfPlotOptions = {
          bar: {
            color: '#f8bc16',
            borderRadius: 4,
            dataLabels: { enabled: true, format: '{y}' },
          },
        };
        let bhcpfLegend = { enabled: false };

        if (this.layerStatus) {
          const uniqueStatuses = [...new Set(data.map((f) => f.emr_status || 'Unknown'))].sort((a, b) => {
            const priority = {
              active: 1, 'fully functional': 1, implemented: 1, 'in progress': 2, 'in-progress': 2, 'partially functional': 2, pending: 3, 'yet to commence': 3, suspended: 4,
            };
            const pa = priority[a.toLowerCase()] || 99;
            const pb = priority[b.toLowerCase()] || 99;
            return pa - pb;
          });

          bhcpfSeries = uniqueStatuses.map((status) => {
            const counts = bhcpfCategories.map((cat) => data.filter((f) => (f.bhcpf_facility || 'Not specified') === cat && (f.emr_status || 'Unknown') === status).length);
            return {
              name: status,
              data: counts,
              color: this.getStatusColor(status),
            };
          });

          bhcpfPlotOptions = {
            bar: {
              stacking: 'normal',
              borderRadius: 4,
              dataLabels: { enabled: true, format: '{y}', style: { textOutline: 'none' } },
            },
          };
          bhcpfLegend = {
            enabled: true,
            itemStyle: { fontFamily: 'Work Sans, sans-serif', fontSize: '11px' },
          };
        } else {
          const bhcpfData = bhcpfCategories.map((cat) => bhcpfMap[cat]);
          bhcpfSeries = [{ name: 'Facilities', data: bhcpfData }];
        }

        this.expandedChart = Highcharts.chart(containerId, {
          chart: {
            type: 'bar',
            backgroundColor: 'transparent',
            height: 480,
          },
          title: {
            text: this.expandedChartTitle,
            style: { fontFamily: 'Work Sans, sans-serif', fontWeight: 'bold', color: '#334155' },
          },
          xAxis: {
            categories: bhcpfCategories,
            labels: { style: { fontSize: '11px', fontFamily: 'Work Sans, sans-serif' } },
          },
          yAxis: {
            min: 0,
            title: { text: 'Facilities Count' },
          },
          legend: bhcpfLegend,
          tooltip: { pointFormat: '<b>{point.y}</b> facilities' },
          plotOptions: bhcpfPlotOptions,
          series: bhcpfSeries,
          exporting: {
            enabled: true,
            buttons: {
              contextButton: {
                menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
              },
            },
            chartOptions: {
              chart: {
                backgroundColor: '#ffffff',
              },
            },
          },
          credits: { enabled: false },
        });
      }
    },
    onModalHidden() {
      if (this.expandedChart) {
        this.expandedChart.destroy();
        this.expandedChart = null;
      }
    },
  },
};
</script>

<style scoped>
/* Page Layout */
.bg-light-gray {
  background-color: #f8f9fa;
  min-height: 100vh;
}
.emr-dashboard-container {
  min-height: 80vh;
}

/* Banner Design System */
.dashboard-banner {
  border-left: 5px solid #007d53;
  border-radius: 8px;
}
.dashboard-title {
  font-size: 32px !important;
}
.dashboard-subtitle {
  font-size: 16px !important;
}
.text-teal {
  color: #007d53 !important;
}
.bg-teal-light {
  background-color: rgba(0, 125, 83, 0.1);
}
.border-left-teal {
  border-left: 4px solid #007d53;
}
.btn-teal {
  background-color: #007d53;
  color: #fff;
  border-color: #007d53;
}
.btn-teal:hover {
  background-color: #00603f;
  color: #fff;
}
.btn-outline-teal {
  color: #007d53;
  border-color: #007d53;
  background-color: transparent;
}
.btn-outline-teal:hover {
  background-color: #007d53;
  color: #fff;
}

/* Metric Cards */
.metric-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #eaeaea;
}
.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05) !important;
}
.metric-label {
  font-size: 10px;
  letter-spacing: 1px;
  font-weight: 700;
}
.metric-value {
  font-size: 24px;
}
.metric-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Colors Utilities */
.text-blue { color: #17b7b5; }
.bg-blue-light { background-color: rgba(23, 183, 181, 0.1); }
.text-green { color: #28a745; }
.bg-green-light { background-color: rgba(40, 167, 69, 0.1); }
.text-orange { color: #fd7e14; }
.bg-orange-light { background-color: rgba(253, 126, 20, 0.1); }

.border-bottom-teal { border-bottom: 3px solid #007d53; }
.border-bottom-blue { border-bottom: 3px solid #17b7b5; }
.border-bottom-green { border-bottom: 3px solid #28a745; }
.border-bottom-orange { border-bottom: 3px solid #fd7e14; }

/* Chart Cards */
.chart-card {
  border: 1px solid #eaeaea;
}
.chart-title {
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
}
.emr-chart-container {
  width: 100%;
  min-height: 100%;
  margin: 0 auto;
  display: block;
}
.emr-chart-scroll-wrapper {
  scrollbar-width: thin;
}
.emr-chart-scroll-wrapper::-webkit-scrollbar {
  width: 6px;
}
.emr-chart-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}
.emr-chart-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.emr-chart-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

@keyframes pulse-flash {
  0% {
    opacity: 0.4;
    color: #6c757d;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    color: #007d53;
    transform: scale(1.2);
  }
  100% {
    opacity: 0.4;
    color: #6c757d;
    transform: scale(1);
  }
}
.flashing-icon {
  animation: pulse-flash 1.5s infinite ease-in-out;
}
.hover-teal:hover {
  color: #007d53 !important;
}

.control-panel-card {
  border-radius: 8px;
  border: 1px solid #eaeaea;
}
.control-panel-title {
  font-size: 16px;
}
.control-panel-card .btn {
  font-size: 14px !important;
}
.custom-premium-select {
  display: block;
  width: 100%;
  height: 42px;
  padding: 0.375rem 2.25rem 0.375rem 0.75rem;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #334155;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23475569' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 12px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
.custom-premium-select:focus {
  border-color: #007d53;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 125, 83, 0.15);
}
.custom-premium-input {
  display: block;
  width: 100%;
  height: 42px;
  padding: 0.375rem 0.75rem;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #334155;
  background-color: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.custom-premium-input::placeholder {
  font-size: 15px;
}
.custom-premium-input:focus {
  border-color: #007d53;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 125, 83, 0.15);
}
.filter-label {
  font-size: 15px;
  letter-spacing: 0.5px;
}
.table-card {
  border-radius: 8px;
}
.table-card-title {
  font-size: 16px;
}
.custom-emr-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}
.custom-emr-table >>> tr {
  transition: background-color 0.2s ease;
}
.custom-emr-table >>> tr:hover {
  background-color: rgba(0, 125, 83, 0.02) !important;
}
.custom-emr-table >>> th {
  font-weight: 700;
  color: #475569;
  font-size: 16px;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 14px 16px;
}
.custom-emr-table >>> td {
  font-size: 16px;
  vertical-align: middle;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
}
.emr-id-badge {
  background-color: #f1f5f9;
  color: #334155;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 16px;
  border: 1px solid #e2e8f0;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}
.status-badge--active {
  background-color: #d1fae5;
  color: #065f46;
}
.status-badge--in-progress {
  background-color: #fef3c7;
  color: #92400e;
}
.status-badge--suspended {
  background-color: #fee2e2;
  color: #991b1b;
}
.status-badge--pending {
  background-color: #f3f4f6;
  color: #374151;
}

/* UI Icons/Animations */
.spin-icon {
  animation: fa-spin 1.5s infinite linear;
}
@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Custom styles override for pagination */
.custom-pagination >>> .page-item.active .page-link {
  background-color: #007d53;
  border-color: #007d53;
  color: white;
}
.custom-pagination >>> .page-link {
  color: #007d53;
}

.gap-2 {
  gap: 8px;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
